import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu,
  X,
  Home,
  Info,
  Heart,
  Brain,
  Flower,
  Users,
  BookOpen,
  Phone,
  Sparkles,
  Library,
  Crown,
  Calendar,
  Newspaper,
  Gift,
  Zap,
  UserPlus,
  ChevronUp,
  ChevronDown,
  LogIn,
  LogOut,
  User,
  Star,
  Bell,
  Search,
  LayoutDashboard,
  MessageSquare,
  KeyRound,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Award,
  Play,
  Bot,
  GitBranch,
  Compass,
  Lightbulb,
  Building2,
  Leaf,
  Video,
  MapPin,
  Dna,
  ShoppingCart,
  Settings,
} from 'lucide-react'
import { cn, formatName, firstName } from '@/lib/utils'
import DisclaimerBanner from './DisclaimerBanner'
import { useAuth } from '@/contexts/AuthContext'
import { useCart } from '@/contexts/CartContext'
import { changePassword } from '@/lib/api'
import { toast } from 'sonner'

interface LayoutProps {
  children: React.ReactNode
}

const navigation = [
  { name: 'Home', href: '/', icon: Home, color: 'text-blue-600' },
  { name: 'Summer Hub 🌞', href: '/summer-hub', icon: Calendar, color: 'text-amber-500', children: [] },
  {
    name: 'Gurukul Offerings',
    href: '#',
    icon: Compass,
    color: 'text-emerald-600',
    wide: true,
    children: [
      { name: 'Gurukul Overview',      href: '/gurukul',               icon: Library,      color: 'text-amber-600',  description: 'Traditional learning ashram'                      },
      { name: 'Age Reversal Yoga',     href: '/gurukul/age-reversal',  icon: Dna,          color: 'text-emerald-500', description: 'Stop epigenetic aging, naturally'                },
      { name: 'Sattva Life Coaching',  href: '/life-coaching',         icon: Sparkles,     color: 'text-amber-500',  description: 'Vedic coaching for clarity & dharma'              },
      { name: 'Yoga Teacher Training', href: '/gurukul/certification', icon: Award,        color: 'text-orange-500', description: '200-hr certified YTT'                             },

      { name: 'Programs',              href: '/programs',              icon: BookOpen,     color: 'text-green-500',  description: 'Courses & certifications'                         },
      { name: 'Resources',             href: '/resources',             icon: Library,      color: 'text-indigo-500', description: 'Guides & learning materials'                      },
    ]
  },
  {
    name: 'Find a Practitioner',
    href: '/marketplace',
    icon: MapPin,
    color: 'text-rose-600',
    children: [
      { name: 'Marketplace',          href: '/marketplace',        icon: Sparkles, color: 'text-amber-500',  description: 'Browse services, book sessions, discover practitioners' },
      { name: 'Practitioner Directory', href: '/find-practitioner', icon: Search,   color: 'text-rose-500',   description: 'Search & view practitioner profiles' },
      { name: 'Join as Practitioner', href: '/onboarding/practitioner', icon: Award, color: 'text-teal-500', description: 'List your services and grow your practice' },
    ]
  },
  {
    name: '30 Minutes Yoga',
    href: '/30minutes-yoga',
    icon: Flower,
    color: 'text-pink-600',
    badge: '30MinutesYoga.com',
    logo: '/images/logos/yoga_lady.jpeg',
    external: true,
    children: [
      { name: '30MinutesYoga Home',  href: '/30minutes-yoga',            icon: Flower,   color: 'text-pink-600',   description: 'Landing page & overview', external: true },
      { name: 'Yoga Classes',        href: '/30minutes-yoga/yoga',       icon: Flower,   color: 'text-pink-500',   description: 'Hatha, Vinyasa & morning flows', external: true },
      { name: 'Meditation Sessions', href: '/30minutes-yoga/meditation', icon: Brain,    color: 'text-purple-500', description: 'Mindfulness & breathwork', external: true },
      { name: 'Chakra Balancing',    href: '/30minutes-yoga/chakras',    icon: Sparkles, color: 'text-indigo-500', description: 'Energy alignment & healing', external: true },
    ]
  },
  {
    name: 'Aryanirog',
    href: '/aryanirog',
    icon: Heart,
    color: 'text-purple-600',
    badge: 'aryanirog.com',
    logo: '/images/logos/green_royal.jpeg',
    external: true,
    children: [
      { name: 'Aryanirog Home',     href: '/aryanirog',               icon: Heart, color: 'text-emerald-600', description: 'Ayurvedic wellness portal', external: true },
      { name: 'Ayurvedic Healing',  href: '/aryanirog/ayurveda',      icon: Heart, color: 'text-rose-500',    description: 'Ancient holistic wellness', external: true },
      { name: 'Dietary Guide',      href: '/aryanirog/dietary-guide', icon: Leaf,  color: 'text-emerald-500', description: 'Food as medicine — Dosha-based nutrition', external: true },
      { name: 'Astrology & Dosha',  href: '/aryanirog/astrology',     icon: Star,  color: 'text-purple-500',  description: 'Jyotish astrology meets Ayurvedic healing', external: true },
    ]
  },
  {
    name: 'Community',
    href: '#',
    icon: Users,
    color: 'text-orange-600',
    children: [
      { name: 'Community Hub',    href: '/community',        icon: MessageSquare, color: 'text-orange-600', description: 'Connect & share with members' },
      { name: 'Cultural Events',  href: '/cultural-events',  icon: Calendar,      color: 'text-yellow-500', description: 'Festivals & gatherings'       },
      { name: 'Royal Magazine',   href: '/royal-magazine',   icon: Newspaper,     color: 'text-teal-500',   description: 'Articles & wisdom'           },
      { name: 'Member Benefits',  href: '/member-benefits',  icon: Gift,          color: 'text-cyan-500',   description: 'Exclusive perks & rewards'   },
      { name: 'Royal Store',      href: '/store',            icon: ShoppingCart,  color: 'text-amber-600',  description: 'Courses, packs & sessions'   },
      { name: 'Our Team',         href: '/team',             icon: Users,         color: 'text-orange-500', description: 'Teachers, guides & healers'  },
    ]
  },
  {
    name: 'Membership',
    href: '#',
    icon: BookOpen,
    color: 'text-indigo-600',
    children: [
      { name: 'Overview',       href: '/membership/overview',         icon: Heart,           color: 'text-rose-500',   description: 'Students & Members paths'        },
      { name: 'Getting Started', href: '/membership/getting-started', icon: Sparkles,        color: 'text-amber-500',  description: 'Onboarding, offerings & registration' },
      { name: 'Member Benefits', href: '/member-benefits',            icon: Star,            color: 'text-yellow-500', description: 'Exclusive perks & rewards'       },
      { name: 'Start Onboarding',  href: '/onboarding',               icon: Compass,         color: 'text-teal-500',   description: 'Pick your path and begin your journey' },
    ]
  },
  {
    name: 'Opportunities',
    href: '#',
    icon: Lightbulb,
    color: 'text-violet-600',
    wide: true,
    children: [
      { name: 'Overview',                href: '/opportunities/overview',           icon: Sparkles,  color: 'text-amber-500',  description: 'Practitioners, Studios & Partners'        },
      { name: 'Platform Overview',       href: '/how-it-works',                    icon: GitBranch, color: 'text-violet-500', description: 'AI-powered wellness hub'                  },
      { name: 'For Members',             href: '/how-it-works/personas?p=member',  icon: User,      color: 'text-blue-500',   description: 'Your personal transformation journey'     },
      { name: 'For Practitioners',        href: '/how-it-works/personas?p=teacher', icon: Award,     color: 'text-green-500',  description: 'AI co-teacher & personal brand'           },
      { name: 'For Studio Owners',       href: '/how-it-works/personas?p=studio',  icon: Building2, color: 'text-orange-500', description: 'Automate & grow your studio'              },
      { name: 'For Licensing Partners',  href: '/how-it-works/personas?p=franchise', icon: Crown,   color: 'text-rose-500',   description: 'Scale multi-location AI operations'       },
      { name: 'Live AI Demo',            href: '/about/workflow-demo',             icon: Play,      color: 'text-green-600',  description: 'See AI agents in action'                  },
      { name: 'Getting Started',         href: '/about/onboarding',               icon: Bot,       color: 'text-blue-600',   description: 'Set up your platform in minutes'          },
    ]
  },
  {
    name: 'Partners',
    href: '#',
    icon: Crown,
    color: 'text-rose-600',
    children: [
      { name: 'Partner Portal',     href: '/franchise',            icon: Crown,    color: 'text-rose-500',    description: 'Manage your locations'         },
      { name: 'Partner Setup', href: '/franchise/onboarding', icon: UserPlus, color: 'text-emerald-500', description: 'Get started fast'               },
      { name: 'AI Marketing',         href: '/ai-marketing',         icon: Zap,      color: 'text-violet-500',  description: 'Automated campaigns & growth'  },
    ]
  },
  {
    name: 'Why Us',
    href: '#',
    icon: Star,
    color: 'text-amber-600',
    children: [
      { name: 'Why Club of Royals', href: '/why-club-of-royals',   icon: Crown,     color: 'text-amber-500',  description: 'Market gaps & our solution'   },
      { name: 'Our Story',          href: '/about',                 icon: Info,      color: 'text-gray-500',   description: 'Mission, values & philosophy' },
      { name: 'Contact',            href: '/contact',               icon: Phone,     color: 'text-slate-500',  description: 'Get in touch with us'         },
      { name: 'Live AI Demo',       href: '/about/workflow-demo',   icon: Play,      color: 'text-green-500',  description: 'See AI agents in action'      },
      { name: 'Getting Started',    href: '/about/onboarding',      icon: Bot,       color: 'text-blue-500',   description: 'Set up in minutes'            },
    ]
  },
]

// ─── Search data ──────────────────────────────────────────────────────────────

interface SearchItem {
  name: string
  href: string
  category: string
  keywords: string[]
  icon: React.ElementType
  description?: string
}

const searchIndex: SearchItem[] = [
  // Pages
  { name: 'Home', href: '/', category: 'Pages', icon: Home, keywords: ['home', 'welcome', 'start'] },
  { name: 'Why Club of Royals',  href: '/why-club-of-royals', category: 'Sales',  icon: Crown, keywords: ['why', 'sales', 'pitch', 'market', 'gaps', 'solution', 'stakeholder', 'enterprise', 'yoga market', 'wellness'] },
  { name: 'Our Story',           href: '/about',               category: 'Pages',       icon: Info,       keywords: ['about', 'story', 'mission', 'vision', 'philosophy', 'history'] },
  { name: 'Contact',             href: '/contact',             category: 'Pages',       icon: Phone,      keywords: ['contact', 'reach', 'email', 'phone', 'support', 'help', 'message'] },
  { name: 'Summer Hub',          href: '/summer-hub',          category: 'Pages',       icon: Calendar,   keywords: ['summer', 'hub', 'season', 'special', 'camp'] },
  // How It Works — AI Platform
  { name: 'How It Works',              href: '/how-it-works',                    category: 'AI Platform', icon: GitBranch,  keywords: ['how it works', 'ai agents', 'platform', 'guide', 'features', 'automation', 'workflow'] },
  { name: 'For Members',               href: '/how-it-works/personas?p=member',  category: 'AI Platform', icon: User,       keywords: ['member', 'transformation', 'journey', 'practice', 'ai recommendations', 'whatsapp', 'voice'] },
  { name: 'For Practitioners',          href: '/how-it-works/personas?p=teacher', category: 'AI Platform', icon: Award,      keywords: ['teacher', 'instructor', 'personal brand', 'ai co-teacher', 'courses', 'curriculum', 'students'] },
  { name: 'For Studio Owners',         href: '/how-it-works/personas?p=studio',  category: 'AI Platform', icon: Building2,  keywords: ['studio', 'owner', 'automation', 'scheduling', 'subscription', 'mrr', 'analytics'] },
  { name: 'For Licensing Partners',    href: '/how-it-works/personas?p=franchise', category: 'AI Platform', icon: Crown,    keywords: ['franchise', 'licensing', 'partner', 'multi-location', 'analytics', 'brand', 'scale', 'ai ops', 'revenue'] },
  { name: 'Live AI Workflow Demo',     href: '/about/workflow-demo',             category: 'AI Platform', icon: Play,       keywords: ['demo', 'workflow', 'agentic', 'live', 'simulation', 'automation', 'agents'] },
  { name: 'Getting Started',           href: '/about/onboarding',               category: 'AI Platform', icon: Bot,        keywords: ['onboarding', 'getting started', 'setup', 'configure', 'tutorial', 'guide', 'steps'] },
  // Classes & Sessions
  { name: 'Yoga Classes', href: '/yoga', category: 'Classes & Sessions', icon: Flower, keywords: ['yoga', 'class', 'hatha', 'vinyasa', 'ashtanga', 'morning', 'evening', 'beginner', 'flow', 'practice'] },
  { name: 'Meditation Sessions', href: '/meditation', category: 'Classes & Sessions', icon: Brain, keywords: ['meditation', 'mindfulness', 'breathing', 'calm', 'focus', 'stress', 'anxiety', 'sleep', 'guided', 'relaxation'] },
  { name: 'Chakra Balancing', href: '/chakras', category: 'Classes & Sessions', icon: Sparkles, keywords: ['chakra', 'energy', 'healing', 'balance', 'aura', 'kundalini', 'reiki', 'spiritual', 'root', 'third eye'] },
  // Healing & Wellness
  { name: 'Ayurvedic Healing', href: '/ayurveda', category: 'Healing & Wellness', icon: Heart, keywords: ['ayurveda', 'herbal', 'detox', 'panchakarma', 'holistic', 'natural', 'healing', 'cleanse', 'dosha', 'pitta', 'vata', 'kapha'] },
  { name: 'Ayurvedic Dietary Guide', href: '/ayurveda/dietary-guide', category: 'Healing & Wellness', icon: Leaf, keywords: ['diet', 'dietary', 'food', 'medicine', 'dosha', 'prakriti', 'vata', 'pitta', 'kapha', 'nutrition', 'ayurveda', 'six tastes', 'bio rhythm'] },
  { name: 'Astrology & Dosha', href: '/ayurveda/astrology', category: 'Healing & Wellness', icon: Star, keywords: ['astrology', 'jyotish', 'kundali', 'zodiac', 'rashi', 'graha', 'dosha', 'whatsapp', 'practitioner', 'matching', 'navagraha'] },
  // Offerings
  { name: 'Services', href: '/services', category: 'Offerings', icon: Heart, keywords: ['service', 'treatment', 'therapy', 'wellness', 'consultation', 'session', '1:1', 'private'] },
  { name: 'Programs', href: '/programs', category: 'Offerings', icon: BookOpen, keywords: ['program', 'course', 'training', 'certification', 'workshop', 'retreat', 'detox', 'weight loss', 'spiritual', 'transformation'] },
  { name: 'Gurukul', href: '/gurukul', category: 'Offerings', icon: Library, keywords: ['gurukul', 'school', 'learning', 'teaching', 'ashram', 'traditional', 'teacher training', 'study'] },
  { name: '200-Hour Yoga Teacher Training', href: '/gurukul/certification', category: 'Offerings', icon: Library, keywords: ['yoga teacher training', 'ytt', '200 hour', 'certification', 'ryt', 'yoga alliance', 'teach yoga', 'become yoga teacher', 'online ytt'] },
  { name: 'Resources', href: '/resources', category: 'Offerings', icon: Library, keywords: ['resource', 'guide', 'ebook', 'download', 'material', 'pdf', 'video', 'audio', 'library'] },
  // Community
  { name: 'Community Hub', href: '/community', category: 'Community', icon: MessageSquare, keywords: ['community', 'hub', 'groups', 'discussion', 'chat', 'feed', 'connect', 'members', 'social', 'live'] },
  { name: 'Our Team', href: '/team', category: 'Community', icon: Users, keywords: ['team', 'teacher', 'instructor', 'guru', 'staff', 'expert', 'therapist', 'healer', 'meet'] },
  { name: 'Cultural Events', href: '/cultural-events', category: 'Community', icon: Calendar, keywords: ['event', 'festival', 'cultural', 'celebration', 'gathering', 'workshop', 'live', 'online', 'webinar', 'satsang'] },
  { name: 'Royal Magazine', href: '/royal-magazine', category: 'Community', icon: Newspaper, keywords: ['magazine', 'article', 'blog', 'news', 'story', 'insight', 'wisdom', 'read', 'journal'] },
  { name: 'Member Benefits', href: '/member-benefits', category: 'Community', icon: Gift, keywords: ['benefit', 'membership', 'discount', 'exclusive', 'privilege', 'reward', 'free', 'perk', 'access', 'royal', 'elite', 'sovereign', 'citizen', 'consultation', 'wellness', 'spiritual', 'ayurvedic', 'business', 'franchise', 'ai marketing', 'pricing', 'plans'] },
  // 30 Minutes Yoga (sub-site)
  { name: '30 Minutes Yoga — Home', href: '/30minutes-yoga', category: '30 Minutes Yoga', icon: Flower, keywords: ['30 minutes yoga', 'quick', 'daily', 'short', 'practice', 'landing'] },
  { name: '30 Minutes Yoga — Classes', href: '/30minutes-yoga/yoga', category: '30 Minutes Yoga', icon: Flower, keywords: ['30 minutes', 'yoga classes', 'hatha', 'vinyasa', 'quick flow'] },
  { name: '30 Minutes Yoga — Meditation', href: '/30minutes-yoga/meditation', category: '30 Minutes Yoga', icon: Brain, keywords: ['30 minutes', 'meditation', 'mindfulness', 'quick'] },
  { name: '30 Minutes Yoga — Chakras', href: '/30minutes-yoga/chakras', category: '30 Minutes Yoga', icon: Sparkles, keywords: ['30 minutes', 'chakra', 'energy', 'balancing'] },
  // Aryanirog (sub-site)
  { name: 'Aryanirog — Home', href: '/aryanirog', category: 'Aryanirog', icon: Heart, keywords: ['aryanirog', 'ayurveda', 'wellness portal', 'landing'] },
  { name: 'Aryanirog — Ayurvedic Healing', href: '/aryanirog/ayurveda', category: 'Aryanirog', icon: Heart, keywords: ['aryanirog', 'ayurveda', 'healing', 'holistic'] },
  { name: 'Aryanirog — Dietary Guide', href: '/aryanirog/dietary-guide', category: 'Aryanirog', icon: Leaf, keywords: ['aryanirog', 'diet', 'food', 'nutrition', 'dosha'] },
  { name: 'Aryanirog — Astrology & Dosha', href: '/aryanirog/astrology', category: 'Aryanirog', icon: Star, keywords: ['aryanirog', 'astrology', 'jyotish', 'dosha', 'zodiac'] },
  // Franchise / Partners
  { name: 'Partner Portal', href: '/franchise', category: 'Partners', icon: Crown, keywords: ['franchise', 'licensing', 'partner', 'business', 'opportunity', 'invest', 'own', 'studio', 'center'] },
  { name: 'Partner Setup', href: '/franchise/onboarding', category: 'Partners', icon: UserPlus, keywords: ['onboarding', 'start', 'setup', 'launch', 'partner', 'licensing', 'new', 'begin'] },
  { name: 'AI Marketing', href: '/ai-marketing', category: 'Partners', icon: Zap, keywords: ['ai', 'marketing', 'automation', 'digital', 'campaign', 'social media', 'growth', 'leads'] },
  // Quick Actions
  { name: 'Join Free', href: '/register', category: 'Quick Actions', icon: Star, keywords: ['join', 'register', 'signup', 'free', 'membership', 'account', 'create', 'new member'] },
  { name: 'Sign In', href: '/login', category: 'Quick Actions', icon: LogIn, keywords: ['login', 'signin', 'account', 'access', 'password', 'my account'] },
  { name: 'Book a Trial Class', href: '/yoga', category: 'Quick Actions', icon: Flower, keywords: ['book', 'trial', 'class', 'start', 'try', 'first class', 'free class', 'beginner'] },
  { name: 'Schedule Consultation', href: '/contact', category: 'Quick Actions', icon: Calendar, keywords: ['schedule', 'consultation', 'appointment', 'book', 'talk', 'call', 'speak'] },
  // Store
  { name: 'Royal Store', href: '/store', category: 'Quick Actions', icon: ShoppingCart, keywords: ['store', 'shop', 'buy', 'course', 'program', 'class pack', 'session', 'workshop', 'pricing', 'purchase', 'cart', 'checkout'] },
  { name: 'Checkout', href: '/checkout', category: 'Quick Actions', icon: ShoppingCart, keywords: ['checkout', 'cart', 'pay', 'payment', 'order', 'purchase'] },
]

const popularSearches = [
  { label: 'Morning Yoga', href: '/yoga' },
  { label: 'Stress Relief Meditation', href: '/meditation' },
  { label: 'Detox Programs', href: '/programs' },
  { label: 'Chakra Healing', href: '/chakras' },
  { label: 'Ayurveda', href: '/ayurveda' },
  { label: 'Cultural Events', href: '/cultural-events' },
  { label: 'Join Free', href: '/register' },
  { label: 'Teacher Training', href: '/gurukul/certification' },
]

// ─── Layout component ──────────────────────────────────────────────────────────

// Base URL for om_open_ui (SSO destination), trailing slash stripped
const OPEN_UI_BASE = ((import.meta.env.VITE_OPEN_UI_PATH as string | undefined) ?? 'http://localhost:3000').replace(/\/$/, '')

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [footerExpanded, setFooterExpanded] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchSelectedIndex, setSearchSelectedIndex] = useState(0)
  const [changePwOpen, setChangePwOpen] = useState(false)
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [showCurrentPw, setShowCurrentPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)
  const [savingPw, setSavingPw] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, logout, token } = useAuth()
  const { itemCount, toggleCart } = useCart()

  // Build an SSO URL that carries the current JWT so om_open_ui can auto-login
  const ssoUrl = (path = '/dashboard') =>
    token ? `${OPEN_UI_BASE}${path}?sso_token=${encodeURIComponent(token)}` : `${OPEN_UI_BASE}${path}`

  const handleChangePassword = async () => {
    if (!currentPw) { toast.error('Please enter your current password'); return }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return }
    setSavingPw(true)
    try {
      const res = await changePassword(currentPw, newPw)
      toast.success(res.message ?? 'Password updated successfully!')
      setCurrentPw(''); setNewPw(''); setConfirmPw('')
      setChangePwOpen(false)
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to update password')
    } finally {
      setSavingPw(false)
    }
  }
  const isFranchiseOwner = user?.role === 'franchise_owner'
  const isNotViewer = user?.role !== 'viewer' // Allow all roles except viewer

  const visibleNavigation = navigation.filter(item => {
    if (item.name === 'Partners' && !isFranchiseOwner) return false
    return true
  })

  const visibleSearchIndex = useMemo(() => searchIndex.filter(item => {
    if (item.category === 'Partners' && !isFranchiseOwner) return false
    return true
  }), [isFranchiseOwner])

  // ⌘K / Ctrl+K to open, Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false)
        setSearchQuery('')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [searchOpen])

  // Auto-focus input when modal opens; reset query on close
  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus())
    } else {
      setSearchQuery('')
      setSearchSelectedIndex(0)
    }
  }, [searchOpen])

  // Reset selection whenever the result set changes
  useEffect(() => {
    setSearchSelectedIndex(0)
  }, [searchQuery])

  // Tokenized, ranked search: every query word must match somewhere
  // (name, category, or keywords) — fixes "doesn't find anything" on
  // multi-word queries like "ai agent" or "yoga teacher".
  const searchResults = useMemo(() => {
    const tokens = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return []

    const scored = visibleSearchIndex.flatMap((item) => {
      const nameLower = item.name.toLowerCase()
      const haystack = `${nameLower} ${item.category.toLowerCase()} ${item.keywords.join(' ')}`

      const allTokensMatch = tokens.every((t) => haystack.includes(t))
      if (!allTokensMatch) return []

      // Score: exact name match > name starts-with > name contains all tokens > keyword-only match
      const fullQuery = tokens.join(' ')
      let score = 0
      if (nameLower === fullQuery) score = 100
      else if (nameLower.startsWith(fullQuery)) score = 80
      else if (tokens.every((t) => nameLower.includes(t))) score = 60
      else score = 40

      return [{ item, score }]
    })

    scored.sort((a, b) => b.score - a.score)
    return scored.map((s) => s.item)
  }, [searchQuery, visibleSearchIndex])

  // Group results by category (preserves score-descending order within each group)
  const groupedResults = useMemo(() => {
    return searchResults.reduce((groups: Record<string, SearchItem[]>, item) => {
      if (!groups[item.category]) groups[item.category] = []
      groups[item.category].push(item)
      return groups
    }, {})
  }, [searchResults])

  // Keyboard navigation within results (Up/Down to move, Enter to select)
  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSearchSelectedIndex((i) => Math.min(i + 1, Math.max(searchResults.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSearchSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      const target = searchResults[searchSelectedIndex] ?? searchResults[0]
      if (target) { navigate(target.href); setSearchOpen(false) }
    }
  }

  // Notify sub-nav components whenever a dropdown opens
  // (fired synchronously from onMouseEnter, not via useEffect, to avoid one-frame lag)
  const dispatchDropdownOpen = () => window.dispatchEvent(new CustomEvent('topNavDropdownOpen'))
  const dispatchDropdownClose = () => window.dispatchEvent(new CustomEvent('topNavDropdownClose'))

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-amber-50">
      {/* Pilot Disclaimer */}
      <DisclaimerBanner />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <nav className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center">
            {/* Logo + badges */}
            <div className="flex items-center gap-2 mr-3 flex-shrink-0">
              <Link to="/" className="flex items-center gap-2.5 group">
                <img
                  src="/images/logos/blue_circular.jpeg"
                  alt="Club of Royals"
                  className="w-9 h-9 rounded-xl object-cover shadow-lg ring-2 ring-amber-200 group-hover:ring-amber-400 transition-all"
                />
                <div className="leading-tight">
                  <span className="text-base font-extrabold text-gray-900 group-hover:text-amber-600 transition-colors" style={{ background: 'linear-gradient(90deg, #b45309, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Club of Royals</span>
                  <span className="hidden sm:block text-[9px] text-gray-400 sanskrit-text leading-none mt-0.5">स्वराज्ये परमो धर्म:</span>
                </div>
              </Link>
              <div className="hidden sm:flex flex-col gap-0.5">
                <a
                  href={isAuthenticated ? ssoUrl('/dashboard') : OPEN_UI_BASE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <Zap className="w-2.5 h-2.5 text-amber-500" />
                  <span className="text-[9px] font-medium text-amber-600 whitespace-nowrap">Powered by <span className="font-bold">omopen.ai</span></span>
                </a>
                <a
                  href="https://www.radhekrishna.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-rose-200 bg-rose-50/50 hover:bg-rose-100 transition-colors"
                >
                  <span className="text-[9px] font-medium text-rose-600 whitespace-nowrap">🙏 Inspired by <span className="font-bold">RadheKrishna.com</span></span>
                </a>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:justify-start lg:space-x-0">
              {/* Home + Summer Hub stacked vertically */}
              <div className="flex flex-col mr-1">
                <Link
                  to="/"
                  className={cn(
                    "flex items-center gap-1 px-1.5 py-0.5 rounded-md text-sm font-medium transition-all duration-150 whitespace-nowrap",
                    location.pathname === '/' ? "bg-amber-50 text-amber-700" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <Home className="w-3 h-3" />
                  <span>Home</span>
                </Link>
                <Link
                  to="/summer-hub"
                  className={cn(
                    "flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium transition-all duration-150 whitespace-nowrap",
                    location.pathname === '/summer-hub' ? "text-amber-600" : "text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                  )}
                >
                  <span>Summer Hub 🌞</span>
                </Link>
              </div>
              {visibleNavigation.filter(item => item.name !== 'Home' && item.name !== 'Summer Hub 🌞').map((item) => {
                const isActive = location.pathname === item.href
                const hasChildren = item.children && item.children.length > 0
                
                if (hasChildren) {
                  return (
                    <div 
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => { setOpenDropdown(item.name); dispatchDropdownOpen() }}
                      onMouseLeave={() => { setOpenDropdown(null); dispatchDropdownClose() }}
                    >
                      <button
                        className={cn(
                          "flex items-center gap-1 px-1.5 py-1 rounded-md text-sm font-medium transition-all duration-150 select-none whitespace-nowrap",
                          openDropdown === item.name
                            ? "bg-amber-50 text-amber-700"
                            : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                        )}
                      >
                        {(item as any).logo && (
                          <img src={(item as any).logo} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                        )}
                        <span>{item.name}</span>
                        <svg className={cn("w-3.5 h-3.5 transition-transform duration-200 flex-shrink-0", openDropdown === item.name ? 'rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {openDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className={cn(
                              "absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-[300]",
                              (item as any).wide ? "w-80" : "w-64"
                            )}
                          >
                            <div className="px-3 py-1.5 mb-1">
                              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{item.name}</span>
                            </div>
                            {'badge' in item && item.badge && (
                              <div className="px-3 pb-2 border-b border-gray-100 mb-1">
                                {item.href && item.href !== '#' ? (
                                  <a
                                    href={item.href as string}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold text-amber-600 tracking-wide uppercase hover:text-amber-700 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >{item.badge as string}</a>
                                ) : (
                                  <span className="text-xs font-semibold text-amber-600 tracking-wide uppercase">{item.badge as string}</span>
                                )}
                              </div>
                            )}
                            {item.children!.map((child) => {
                              const ChildIcon = child.icon
                              const childIsActive = location.pathname === child.href || (child.href !== '/' && location.pathname.startsWith(child.href.split('?')[0]))
                              const commonClass = cn(
                                "flex items-start gap-3 mx-1.5 px-3 py-2.5 rounded-lg text-sm transition-all duration-100 group",
                                childIsActive ? "bg-amber-50 text-amber-700" : "text-gray-700 hover:bg-gray-50"
                              )
                              const innerContent = (
                                <>
                                  <div className={cn(
                                    "mt-0.5 p-1.5 rounded-md flex-shrink-0 transition-colors",
                                    childIsActive ? "bg-amber-100" : "bg-gray-100 group-hover:bg-amber-50"
                                  )}>
                                    {(child as any).logo ? (
                                      <img src={(child as any).logo} alt="" className="h-3.5 w-3.5 rounded-full object-cover" />
                                    ) : (
                                      <ChildIcon className={cn("h-3.5 w-3.5", childIsActive ? "text-amber-600" : "text-gray-500 group-hover:text-amber-600")} />
                                    )}
                                  </div>
                                  <div>
                                    <span className="font-medium block leading-tight">{child.name}</span>
                                    {(child as any).description && (
                                      <span className="text-xs text-gray-400 mt-0.5 block">{(child as any).description}</span>
                                    )}
                                  </div>
                                </>
                              )
                              return (child as any).external ? (
                                <a
                                  key={child.name}
                                  href={child.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={commonClass}
                                >
                                  {innerContent}
                                </a>
                              ) : (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  className={commonClass}
                                >
                                  {innerContent}
                                </Link>
                              )
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                }
                
                if ((item as any).featured) {
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold transition-all duration-150 whitespace-nowrap border",
                        isActive
                          ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                          : "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400"
                      )}
                    >
                      <span>{item.name}</span>
                    </Link>
                  )
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-1 px-1.5 py-1 rounded-md text-sm font-medium transition-all duration-150 whitespace-nowrap",
                      isActive
                        ? "bg-amber-50 text-amber-700"
                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    )}
                  >
                    <span>{item.name}</span>
                  </Link>
                )
              })}
            </div>

            {/* Right-side CTA group */}
            <div className="hidden lg:flex items-center gap-2 ml-auto flex-shrink-0">
              {/* Search trigger */}
              <button
                onClick={() => setSearchOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-lg text-xs font-medium transition-colors border border-gray-200"
              >
                <Search className="w-3.5 h-3.5" />
                <span className="text-gray-400">Search</span>
                <kbd className="hidden xl:inline ml-1 px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-400">⌘K</kbd>
              </button>

              {/* Cart icon */}
              <button
                onClick={toggleCart}
                className="relative p-2 text-sage-500 hover:text-sage-800 hover:bg-sage-100 rounded-lg transition-colors"
                aria-label="Shopping cart"
              >
                <ShoppingCart className="w-4 h-4" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </button>

              {/* Notification bell — show only when authenticated */}
              {isAuthenticated && (
                <button className="relative p-2 text-sage-500 hover:text-sage-800 hover:bg-sage-100 rounded-lg transition-colors">
                  <Bell className="w-4 h-4" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></span>
                </button>
              )}

              {!isAuthenticated ? (
                <>
                  {/* Sign In */}
                  <Link
                    to="/login"
                    className="flex items-center space-x-1.5 px-4 py-2 text-sm font-semibold text-sage-700 hover:text-sage-900 hover:bg-sage-100 rounded-lg border border-sage-200 transition-all"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </Link>

                  {/* Join Free CTA */}
                  <Link
                    to="/register"
                    className="flex items-center space-x-1.5 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-lg shadow-sm hover:shadow-md transition-all"
                  >
                    <Star className="w-4 h-4" />
                    <span>Join Free</span>
                  </Link>
                </>
              ) : (
                /* User avatar dropdown */
                <div
                  className="relative"
                  onMouseEnter={() => { setUserMenuOpen(true); dispatchDropdownOpen() }}
                  onMouseLeave={() => { setUserMenuOpen(false); dispatchDropdownClose() }}
                >
                  <button className="flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-sage-100 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm flex-shrink-0">
                      <span className="text-xs font-bold text-white">
                        {firstName(user?.full_name ?? user?.username).charAt(0).toUpperCase() || <User className="w-4 h-4" />}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-sage-700 max-w-[96px] truncate">
                      {formatName(user?.full_name) || formatName(user?.username) || user?.email?.split('@')[0] || 'Member'}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-sage-400" />
                  </button>

                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-xl border border-sage-200 py-2 z-[300]"
                      >
                        <div className="px-4 py-3 border-b border-sage-100 mb-1">
                          {(user?.full_name || user?.username) && (
                            <p className="text-sm font-bold text-sage-900 truncate mb-0.5">
                              {formatName(user?.full_name) || formatName(user?.username)}
                            </p>
                          )}
                          <p className="text-xs text-sage-400 truncate">{user?.email}</p>
                          {(() => {
                            const pkg = (user as any)?.selected_package
                            if (!pkg) return null

                            // For practitioners: show their role type label, not the subscription plan label.
                            const practitionerTypeMap: Record<string, { label: string; icon: string; cls: string }> = {
                              yoga_teacher:          { label: 'Yoga Teacher',       icon: '🧘', cls: 'bg-teal-100 text-teal-700 border-teal-200'   },
                              ayurveda_practitioner: { label: 'Ayurveda Expert',    icon: '🌿', cls: 'bg-green-100 text-green-700 border-green-200' },
                              astrologer:            { label: 'Astrologer',         icon: '⭐', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
                              health_coach:          { label: 'Health Coach',       icon: '💪', cls: 'bg-blue-100 text-blue-700 border-blue-200'    },
                              life_coach:            { label: 'Life Coach',         icon: '🦋', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
                              studio:                { label: 'Studio Owner',       icon: '🏠', cls: 'bg-rose-100 text-rose-700 border-rose-200'    },
                              franchise_partner:     { label: 'Franchise Partner',  icon: '🏛️', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
                            }

                            const tierMap: Record<string, { label: string; icon: string; cls: string }> = {
                              // Member-facing tiers (Club of Royals)
                              royal_sovereign: { label: 'Royal Sovereign',  icon: '💎', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
                              royal_elite:     { label: 'Royal Elite',      icon: '👑', cls: 'bg-amber-100 text-amber-700 border-amber-200'  },
                              royal_citizen:   { label: 'Royal Citizen',    icon: '🌿', cls: 'bg-green-100 text-green-700 border-green-200'  },
                              // Practitioner professional plan tiers (fallback when no practitioner_type)
                              wellness:        { label: 'Studio Director',  icon: '🧘', cls: 'bg-teal-100 text-teal-700 border-teal-200'    },
                              studio:          { label: 'Solo Practitioner',icon: '🪷', cls: 'bg-rose-100 text-rose-700 border-rose-200'     },
                              enterprise:      { label: 'Franchise Partner',icon: '🏛️', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200'},
                              // Fallbacks
                              free:            { label: 'Free',             icon: '🌱', cls: 'bg-sage-100 text-sage-600 border-sage-200'    },
                              free_trial:      { label: 'Free Trial',       icon: '🎁', cls: 'bg-sage-100 text-sage-600 border-sage-200'    },
                            }

                            // Practitioners show their professional type (e.g. "Yoga Teacher"),
                            // with an employment suffix when they have a dual profile.
                            const practType = (user as any)?.practitioner_type
                            const empType = (user as any)?.employment_type
                            let t = practType && practitionerTypeMap[practType]
                              ? { ...practitionerTypeMap[practType] }
                              : tierMap[pkg] ?? { label: pkg, icon: '✨', cls: 'bg-sage-100 text-sage-600 border-sage-200' }

                            // Dual-profile practitioners get a subtle "(+ Solo)" suffix
                            const label = (practType && empType === 'both')
                              ? `${t.label} · Solo`
                              : t.label

                            return (
                              <Link
                                to="/member-benefits"
                                className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${t.cls} hover:opacity-80 transition-opacity`}
                                title="View or change your membership tier"
                              >
                                <span>{t.icon}</span>
                                <span>{label}</span>
                              </Link>
                            )
                          })()}
                        </div>
                        {/* Persona-specific primary dashboard */}
                        {user?.role === 'franchise_owner' && (
                          <Link to="/partner/dashboard" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors">
                            <LayoutDashboard className="w-4 h-4 text-purple-500" />
                            <span>Partner Dashboard</span>
                            <span className="ml-auto text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">👑</span>
                          </Link>
                        )}
                        {user?.role === 'studio_admin' && (
                          <Link to="/studio/admin/dashboard" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors">
                            <LayoutDashboard className="w-4 h-4 text-orange-500" />
                            <span>Studio Dashboard</span>
                            <span className="ml-auto text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-semibold">🏛️</span>
                          </Link>
                        )}
                        {user?.role === 'counselor' && (
                          <Link to="/teacher/dashboard" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors">
                            <LayoutDashboard className="w-4 h-4 text-green-500" />
                            <span>Teacher Dashboard</span>
                            <span className="ml-auto text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">🧘</span>
                          </Link>
                        )}
                        {isNotViewer && user?.role !== 'franchise_owner' && user?.role !== 'studio_admin' && user?.role !== 'counselor' && (
                          <a
                            href={ssoUrl('/dashboard')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors"
                          >
                            <Zap className="w-4 h-4 text-amber-500" />
                            <span>AI Platform</span>
                            <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">SSO</span>
                          </a>
                        )}
                        {/* Yoga Studio — live session hub accessible to all non-viewer roles */}
                        {isNotViewer && (
                          <Link to="/studio/dashboard?tab=yoga_studio" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors border-t border-sage-100 mt-1 pt-2">
                            <Video className="w-4 h-4 text-indigo-500" />
                            <span>Yoga Studio</span>
                            <span className="ml-auto text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                              <span className="w-1 h-1 rounded-full bg-green-500 animate-pulse inline-block" /> Live
                            </span>
                          </Link>
                        )}
                        {!isNotViewer && (
                          <Link to="/member/dashboard" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors">
                            <LayoutDashboard className="w-4 h-4 text-amber-500" />
                            <span>My Dashboard</span>
                          </Link>
                        )}
                        <Link to="/member-benefits" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors">
                          <Star className="w-4 h-4 text-amber-500" />
                          <span>Member Benefits</span>
                        </Link>
                        <Link to="/messages" className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors">
                          <MessageSquare className="w-4 h-4 text-blue-500" />
                          <span>Messages</span>
                        </Link>
                        <Link
                          to={
                            user?.role === 'counselor' || user?.role === 'instructor'
                              ? '/teacher/dashboard?tab=settings'
                              : user?.role === 'studio_admin' || user?.role === 'client_owner'
                              ? '/studio/admin/dashboard?tab=settings'
                              : '/messages?tab=settings'
                          }
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors"
                        >
                          <Settings className="w-4 h-4 text-sage-400" />
                          <span>Settings</span>
                        </Link>
                        <Link
                          to="/security"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 transition-colors"
                        >
                          <ShieldCheck className="w-4 h-4 text-sage-400" />
                          <span>Security</span>
                        </Link>
                        <div className="border-t border-sage-100 mt-1 pt-1">
                          <button
                            onClick={logout}
                            className="flex items-center space-x-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors w-full text-left"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* Mobile cart + search triggers */}
          <div className="md:hidden flex items-center gap-1 absolute top-4 left-14">
            <button onClick={toggleCart} className="relative p-2 text-sage-500 hover:text-sage-800 hover:bg-sage-100 rounded-lg transition-colors">
              <ShoppingCart className="w-4 h-4" />
              {itemCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{itemCount > 9 ? '9+' : itemCount}</span>}
            </button>
            <button
              onClick={() => setSearchOpen(o => !o)}
              className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile menu button - positioned absolutely */}
          <div className="md:hidden absolute top-4 right-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-sage-600 hover:text-sage-800 hover:bg-sage-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sage-500"
            >
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-sage-200"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {visibleNavigation.map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.href
                  const hasChildren = item.children && item.children.length > 0
                  
                  return (
                    <div key={item.name}>
                      {hasChildren && 'href' in item && item.href && item.href !== '#' ? (
                        <div className="flex items-center">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                            className={cn(
                              "flex items-center space-x-2 px-3 py-2 text-base font-bold rounded-md transition-all duration-200 flex-1",
                              isActive ? "text-sage-700 bg-sage-100 shadow-sm" : "text-sage-600 hover:text-sage-800 hover:bg-sage-50"
                            )}
                          >
                            <Icon className={`w-5 h-5 ${item.color}`} />
                            <span>{item.name}</span>
                            <svg className={cn("w-4 h-4 ml-auto transition-transform", openDropdown === item.name ? "rotate-180" : "")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <a
                            href={item.href as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-2 text-xs text-amber-600 font-semibold hover:underline"
                          >↗</a>
                        </div>
                      ) : (
                        <Link
                          to={hasChildren ? '#' : item.href}
                          onClick={() => hasChildren && setOpenDropdown(openDropdown === item.name ? null : item.name)}
                          className={cn(
                            "flex items-center space-x-2 px-3 py-2 text-base font-bold rounded-md transition-all duration-200 w-full",
                            isActive
                              ? "text-sage-700 bg-sage-100 shadow-sm"
                              : "text-sage-600 hover:text-sage-800 hover:bg-sage-50"
                          )}
                        >
                          <Icon className={`w-5 h-5 ${item.color}`} />
                          <span>{item.name}</span>
                          {hasChildren && (
                            <svg 
                              className={cn("w-4 h-4 ml-auto transition-transform", openDropdown === item.name ? "rotate-180" : "")} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </Link>
                      )}
                      
                      {/* Mobile Dropdown */}
                      <AnimatePresence>
                        {hasChildren && openDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ml-6 mt-1 space-y-1"
                          >
                            {item.children!.map((child) => {
                              const ChildIcon = child.icon
                              const childIsActive = location.pathname === child.href
                              const mobileClass = cn(
                                "flex items-center space-x-2 px-3 py-2 text-sm font-bold rounded-md transition-all duration-200",
                                childIsActive ? "text-sage-700 bg-sage-100 shadow-sm" : "text-sage-600 hover:text-sage-800 hover:bg-sage-50"
                              )
                              return (child as any).external ? (
                                <a
                                  key={child.name}
                                  href={child.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={mobileClass}
                                >
                                  <ChildIcon className={`w-4 h-4 ${child.color}`} />
                                  <span>{child.name}</span>
                                </a>
                              ) : (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={mobileClass}
                                >
                                  <ChildIcon className={`w-4 h-4 ${child.color}`} />
                                  <span>{child.name}</span>
                                </Link>
                              )
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>

              {/* Mobile auth buttons */}
              <div className="pt-3 pb-2 border-t border-sage-200 px-2 space-y-2">
                {!isAuthenticated ? (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 text-sm font-semibold text-sage-700 border border-sage-200 rounded-lg hover:bg-sage-50 transition-colors"
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Sign In</span>
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-sm transition-all"
                    >
                      <Star className="w-4 h-4" />
                      <span>Join Free</span>
                    </Link>
                  </>
                ) : (
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs text-sage-400">Signed in as <span className="font-semibold text-sage-600">{user?.email}</span></div>
                    {user?.role === 'franchise_owner' && (
                      <Link to="/partner/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 px-3 py-2 text-sm text-sage-600 hover:bg-sage-50 rounded-md transition-colors">
                        <LayoutDashboard className="w-4 h-4 text-purple-500" /><span>Partner Dashboard 👑</span>
                      </Link>
                    )}
                    {user?.role === 'studio_admin' && (
                      <Link to="/studio/admin/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 px-3 py-2 text-sm text-sage-600 hover:bg-sage-50 rounded-md transition-colors">
                        <LayoutDashboard className="w-4 h-4 text-orange-500" /><span>Studio Dashboard 🏛️</span>
                      </Link>
                    )}
                    {user?.role === 'counselor' && (
                      <Link to="/teacher/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 px-3 py-2 text-sm text-sage-600 hover:bg-sage-50 rounded-md transition-colors">
                        <LayoutDashboard className="w-4 h-4 text-green-500" /><span>Teacher Dashboard 🧘</span>
                      </Link>
                    )}
                    {isNotViewer && user?.role !== 'franchise_owner' && user?.role !== 'studio_admin' && user?.role !== 'counselor' && (
                      <a
                        href={ssoUrl('/dashboard')}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-sage-600 hover:bg-sage-50 rounded-md transition-colors"
                      >
                        <Zap className="w-4 h-4 text-amber-500" />
                        <span>AI Platform</span>
                        <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">SSO</span>
                      </a>
                    )}
                    {/* Yoga Studio — common live session hub for all non-viewer roles */}
                    {isNotViewer && (
                      <Link to="/studio/dashboard?tab=yoga_studio" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 px-3 py-2 text-sm text-sage-600 hover:bg-sage-50 rounded-md transition-colors border-t border-sage-100 mt-1 pt-2">
                        <Video className="w-4 h-4 text-indigo-500" />
                        <span>Yoga Studio</span>
                        <span className="ml-auto text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                          <span className="w-1 h-1 rounded-full bg-green-500 animate-pulse inline-block" /> Live
                        </span>
                      </Link>
                    )}
                    {!isNotViewer && (
                      <Link to="/member/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 px-3 py-2 text-sm text-sage-600 hover:bg-sage-50 rounded-md transition-colors">
                        <LayoutDashboard className="w-4 h-4 text-amber-500" /><span>My Dashboard</span>
                      </Link>
                    )}
                    <button onClick={() => { logout(); setMobileMenuOpen(false) }} className="flex items-center space-x-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-md transition-colors w-full text-left">
                      <LogOut className="w-4 h-4" /><span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer
        className={cn(
          'relative mt-auto border-t-2 border-amber-900/30 shadow-[0_-4px_20px_rgba(180,130,30,0.15)]',
          'text-white overflow-hidden transition-[max-height,padding] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]',
          footerExpanded ? 'max-h-[700px] py-12 pb-6' : 'max-h-[68px] py-0'
        )}
        style={{ background: 'linear-gradient(160deg, #0a1940 0%, #06102e 50%, #040d20 100%)' }}
      >
        {/* Ambient glow overlay */}
        <div
          className={cn('absolute inset-0 pointer-events-none transition-opacity duration-500', footerExpanded ? 'opacity-100' : 'opacity-0')}
          style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(180,130,30,0.12) 0%, transparent 55%), radial-gradient(ellipse at 80% 20%, rgba(217,155,26,0.08) 0%, transparent 50%)' }}
        />

        {/* Collapsed preview bar */}
        <div className={cn('absolute inset-x-0 top-0 flex items-center justify-center h-[68px] transition-opacity duration-300', footerExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100')}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img src="/images/logos/blue_circular.jpeg" alt="OM" className="h-6 w-6 rounded-full object-cover ring-1 ring-amber-400/40" />
              <span className="text-sm font-semibold text-white/90">Club of Royals</span>
            </div>
            <span className="text-white/40 text-xs">·</span>
            <span className="text-xs text-white/60">Ancient Wisdom. AI Intelligence. One Platform.</span>
          </div>
        </div>

        {/* Expand / Collapse toggle button */}
        <button
          onClick={() => setFooterExpanded(e => !e)}
          className="absolute top-3 right-5 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-medium backdrop-blur-sm hover:bg-white/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
        >
          <ChevronUp className={cn('h-3.5 w-3.5 transition-transform duration-300', footerExpanded ? '' : 'rotate-180')} />
          {footerExpanded ? 'Collapse' : 'Expand'}
        </button>

        {/* Expanded content */}
        <div className={cn('relative max-w-7xl mx-auto px-6 lg:px-8 transition-opacity duration-300', footerExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">

            {/* Brand column */}
            <div>
              <Link to="/" className="inline-flex items-center gap-2.5 mb-4 group">
                <img src="/images/logos/blue_circular.jpeg" alt="OM" className="h-8 w-8 rounded-full object-cover ring-1 ring-amber-400/40" />
                <div>
                  <p className="text-base font-bold text-white leading-none">Club of Royals</p>
                  <p className="text-[10px] text-white/60 leading-none mt-0.5 sanskrit-text">स्वराज्ये परमो धर्म:</p>
                </div>
              </Link>
              <p className="text-sm text-white/75 leading-relaxed mb-4">
                The only wellness platform where 5,000 years of yogic wisdom meets AI-powered transformation.
                Practice, teach, or scale your studio — all in one place.
              </p>
              <div className="flex gap-2.5 mt-4">
                {[
                  { emoji: '💼', href: 'https://www.linkedin.com/in/om-open-ai/', label: 'LinkedIn' },
                  { emoji: '📷', href: 'https://www.instagram.com/clubofroyals', label: 'Instagram' },
                  { emoji: '📘', href: 'https://www.facebook.com/groups/481236248609766', label: 'Facebook' },
                  { emoji: '⚡', href: isAuthenticated ? ssoUrl('/dashboard') : OPEN_UI_BASE, label: 'AI Platform' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-base hover:bg-white/20 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
                    {s.emoji}
                  </a>
                ))}
              </div>
              <a
                href={isAuthenticated ? ssoUrl('/dashboard') : OPEN_UI_BASE}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
              >
                <Zap className="w-3 h-3 text-amber-400" />
                <span className="text-xs font-semibold text-amber-300">Powered by omopen.ai</span>
              </a>
            </div>

            {/* Explore */}
            <div>
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-widest mb-4 relative pb-2 after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-amber-500/50 after:rounded">Explore</h3>
              <ul className="space-y-2.5">
                {[
                  { label: 'Yoga Classes', href: '/yoga' },
                  { label: 'Meditation Sessions', href: '/meditation' },
                  { label: 'Chakra Balancing', href: '/chakras' },
                  { label: 'Ayurvedic Healing', href: '/ayurveda' },
                  { label: 'Royal Store 🛒', href: '/store' },
                  { label: 'Programs', href: '/programs' },
                  { label: 'Resources', href: '/resources' },
                ].map(({ label, href }) => (
                  <li key={href}>
                    <Link to={href} className="flex items-center gap-2 text-sm text-white/75 hover:text-white transition-all duration-200 hover:translate-x-1 group">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Community */}
            <div>
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-widest mb-4 relative pb-2 after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-amber-500/50 after:rounded">Community</h3>
              <ul className="space-y-2.5">
                {[
                  { label: 'Meet the Team', href: '/team' },
                  { label: 'Cultural Events', href: '/cultural-events' },
                  { label: 'Royal Magazine', href: '/royal-magazine' },
                  { label: 'Member Benefits', href: '/member-benefits' },
                  { label: 'Gurukul', href: '/gurukul' },
                  { label: '200-Hr YTT', href: '/gurukul/certification' },
                  { label: 'About Us', href: '/about' },
                ].map(({ label, href }) => (
                  <li key={href}>
                    <Link to={href} className="flex items-center gap-2 text-sm text-white/75 hover:text-white transition-all duration-200 hover:translate-x-1 group">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact & Franchise */}
            <div>
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-widest mb-4 relative pb-2 after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-amber-500/50 after:rounded">Contact &amp; Partners</h3>
              <ul className="space-y-2.5">
                {[
                  { label: 'Contact Us', href: '/contact' },
                  ...(isFranchiseOwner ? [
                    { label: 'Partner Portal', href: '/franchise' },
                    { label: 'Partner Setup', href: '/franchise/onboarding' },
                  ] : []),
                ].map(({ label, href }) => (
                  <li key={href}>
                    <Link to={href} className="flex items-center gap-2 text-sm text-white/75 hover:text-white transition-all duration-200 hover:translate-x-1 group">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-4 border-t border-white/15 space-y-4">
                <div>
                  <p className="text-xs text-white/60 mb-1.5 font-medium uppercase tracking-wider">Location</p>
                  <a href="https://maps.google.com/?q=Eden+Prairie+Middle+School,+Eden+Prairie,+MN+55347" target="_blank" rel="noopener noreferrer" className="text-sm text-white/75 hover:text-white transition-colors leading-relaxed block">
                    📍 Yoga Schedule @ Eden Prairie Middle School<br />
                    <span className="text-white/55">Eden Prairie, MN 55347, USA</span>
                  </a>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-1.5 font-medium uppercase tracking-wider">Phone</p>
                  <ul className="space-y-1">
                    <li><a href="tel:+18555505010" className="text-sm text-white/75 hover:text-white transition-colors">📱 +1 (855) 550-5010</a></li>
                    <li><a href="tel:+16128000898" className="text-sm text-white/75 hover:text-white transition-colors">📱 +1 (612) 800-0898</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Legal links row */}
          <div className="mt-10 pt-6 border-t border-white/15">
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-4">
              {[
                { label: 'Privacy Policy',    href: '/privacy' },
                { label: 'Terms of Service',  href: '/terms' },
                { label: 'Cookie Policy',     href: '/cookies' },
                { label: 'Accessibility',     href: '/accessibility' },
              ].map(({ label, href }) => (
                <Link key={href} to={href} className="text-xs text-white/50 hover:text-white transition-colors flex items-center gap-1.5">
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Copyright bar */}
          <div className="text-center relative">
            <p className="text-sm font-medium text-white/85">
              © 2026 Club of Royals, LLC. All rights reserved.
            </p>
            <p className="text-xs text-white/55 mt-1.5 flex flex-wrap justify-center gap-x-3 gap-y-1">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <span className="text-white/25">·</span>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <span className="text-white/25">·</span>
              <Link to="/member-benefits" className="hover:text-white transition-colors">Member Benefits</Link>
              {isFranchiseOwner && <>
                <span className="text-white/25">·</span>
                <Link to="/franchise/onboarding" className="hover:text-white transition-colors">Partners</Link>
              </>}
            </p>
            <p className="text-xs text-white/40 mt-1.5 sanskrit-text">
              &quot;स्वराज्ये परमो धर्म:&quot; — Self-sovereignty is the highest duty
            </p>
          </div>
        </div>
      </footer>
      {/* ─── Search backdrop (click-outside to close) ────────────────────────── */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[590] bg-black/20 backdrop-blur-[2px]"
            onClick={() => setSearchOpen(false)}
          >
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Search panel — rendered once, fixed & centered, reachable from both desktop and mobile triggers ─── */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="fixed left-1/2 top-[10vh] -translate-x-1/2 w-[92vw] max-w-[540px] max-h-[72vh] bg-white rounded-2xl shadow-2xl border border-gray-200 z-[600] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Input row */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-sage-100 shrink-0">
              <Search className="w-5 h-5 text-sage-400 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search classes, programs, healing, events…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="flex-1 text-sage-800 placeholder-sage-400 outline-none text-base bg-transparent"
              />
              {searchQuery ? (
                <button
                  onClick={() => setSearchQuery('')}
                  className="p-1 text-sage-400 hover:text-sage-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <kbd className="hidden sm:flex items-center px-1.5 py-0.5 text-xs text-sage-400 bg-sage-50 border border-sage-200 rounded font-mono">esc</kbd>
              )}
            </div>

            {/* Results */}
            <div className="overflow-y-auto flex-1">
              {!searchQuery.trim() ? (
                <div className="p-4 space-y-5">
                  <div>
                    <p className="text-[11px] font-semibold text-sage-400 uppercase tracking-wider mb-2.5">Popular Searches</p>
                    <div className="flex flex-wrap gap-2">
                      {popularSearches.map((item) => (
                        <button
                          key={item.label}
                          onClick={() => { navigate(item.href); setSearchOpen(false) }}
                          className="px-3 py-1.5 text-sm text-sage-600 bg-sage-50 hover:bg-amber-50 hover:text-amber-700 border border-sage-200 hover:border-amber-300 rounded-full transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-sage-400 uppercase tracking-wider mb-2.5">Quick Actions</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {searchIndex.filter((i) => i.category === 'Quick Actions').map((item) => {
                        const Icon = item.icon
                        return (
                          <button
                            key={item.name}
                            onClick={() => { navigate(item.href); setSearchOpen(false) }}
                            className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sage-600 hover:bg-sage-50 transition-colors text-left"
                          >
                            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                              <Icon className="w-3.5 h-3.5 text-amber-600" />
                            </div>
                            <span className="text-sm font-medium">{item.name}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="py-14 text-center">
                  <Search className="w-10 h-10 text-sage-200 mx-auto mb-3" />
                  <p className="text-sage-500 text-sm">
                    No results for <span className="font-semibold text-sage-700">&ldquo;{searchQuery}&rdquo;</span>
                  </p>
                  <p className="text-xs text-sage-400 mt-1">Try yoga, meditation, programs, chakra, events…</p>
                </div>
              ) : (
                <div className="py-2">
                  {Object.entries(groupedResults).map(([category, items]) => (
                    <div key={category}>
                      <p className="px-4 pt-3 pb-1.5 text-[11px] font-semibold text-sage-400 uppercase tracking-wider">
                        {category}
                      </p>
                      {items.map((item) => {
                        const Icon = item.icon
                        const globalIndex = searchResults.findIndex((r) => r.name === item.name && r.href === item.href)
                        const isSelected = globalIndex === searchSelectedIndex
                        const tokens = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean)
                        const nameLower = item.name.toLowerCase()
                        const firstToken = tokens.find((t) => nameLower.includes(t))
                        const idx = firstToken ? nameLower.indexOf(firstToken) : -1
                        const highlighted =
                          idx >= 0 && firstToken ? (
                            <>
                              {item.name.slice(0, idx)}
                              <mark className="bg-amber-100 text-amber-800 rounded px-0.5">
                                {item.name.slice(idx, idx + firstToken.length)}
                              </mark>
                              {item.name.slice(idx + firstToken.length)}
                            </>
                          ) : (
                            item.name
                          )
                        return (
                          <button
                            key={item.name}
                            onClick={() => { navigate(item.href); setSearchOpen(false) }}
                            onMouseEnter={() => setSearchSelectedIndex(globalIndex)}
                            className={cn(
                              "flex items-center space-x-3 w-full px-4 py-2.5 transition-colors text-left group",
                              isSelected ? "bg-amber-50" : "hover:bg-sage-50"
                            )}
                          >
                            <div className={cn(
                              "w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border",
                              isSelected ? "bg-white border-amber-200" : "bg-sage-50 group-hover:bg-white border-sage-100"
                            )}>
                              <Icon className={cn("w-3.5 h-3.5", isSelected ? "text-amber-600" : "text-sage-500")} />
                            </div>
                            <span className="text-sm text-sage-700 font-medium flex-1">{highlighted}</span>
                            <span className={cn(
                              "text-xs text-sage-400 transition-opacity",
                              isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}>
                              {item.href}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2.5 border-t border-sage-100 flex items-center justify-between text-[11px] text-sage-400 shrink-0 bg-sage-50/50">
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white border border-sage-200 rounded font-mono">↑↓</kbd> navigate
                &nbsp;·&nbsp;
                <kbd className="px-1.5 py-0.5 bg-white border border-sage-200 rounded font-mono">↵</kbd> select
                &nbsp;·&nbsp;
                <kbd className="px-1.5 py-0.5 bg-white border border-sage-200 rounded font-mono">esc</kbd> close
              </span>
              <span className="hidden sm:inline">
                <kbd className="px-1.5 py-0.5 bg-white border border-sage-200 rounded font-mono">⌘K</kbd> anytime
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Change Password Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {changePwOpen && (
          <div
            className="fixed inset-0 z-[700] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) { setChangePwOpen(false); setCurrentPw(''); setNewPw(''); setConfirmPw('') } }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="relative bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
            >
              <button
                onClick={() => { setChangePwOpen(false); setCurrentPw(''); setNewPw(''); setConfirmPw('') }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-sage-900">Change Password</h2>
                  <p className="text-sm text-sage-500">{user?.email}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-sage-700 mb-1">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="Enter current password"
                      className="w-full px-4 py-2.5 pr-10 rounded-xl border border-sage-200 focus:outline-none focus:ring-2 focus:ring-amber-300 text-sm"
                    />
                    <button type="button" onClick={() => setShowCurrentPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-400 hover:text-sage-700">
                      {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-sage-700 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full px-4 py-2.5 pr-10 rounded-xl border border-sage-200 focus:outline-none focus:ring-2 focus:ring-amber-300 text-sm"
                    />
                    <button type="button" onClick={() => setShowNewPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-400 hover:text-sage-700">
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-sage-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full px-4 py-2.5 rounded-xl border border-sage-200 focus:outline-none focus:ring-2 focus:ring-amber-300 text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setChangePwOpen(false); setCurrentPw(''); setNewPw(''); setConfirmPw('') }}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-sage-200 text-sage-700 text-sm font-medium hover:bg-sage-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={savingPw}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:brightness-105 text-white text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {savingPw
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                    : <><ShieldCheck className="w-4 h-4" /> Update Password</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Layout